import { Trans } from "@lingui/react/macro";
import {
  informationFieldConfigValueSchema,
  updatePrimaryResponsible,
} from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useLingui } from "@lingui/react/macro";
import Select from "@medoma/components/Select/Select";
import { useEmployees } from "@/api/Employee";
import styles from "./EditArea.module.scss";
import { employeeName } from "@medoma/models/shifts";
import type z from "zod";

export const EditPrimaryResponsible = ({
  currentPrimaryResponsible,
  onSuccess,
  patientId,
  fieldConfig,
}: {
  currentPrimaryResponsible: number | null | undefined;
  onSuccess: () => void;
  patientId: string;
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      primaryResponsible: currentPrimaryResponsible,
    },
  });

  const NO_EMPLOYEE = "";
  const queryClient = useQueryClient();
  const {
    data: employees,
    isPending: isPendingEmployees,
    isError: isErrorEmployees,
  } = useEmployees();

  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: async (primaryResponsible: number | null | undefined) => {
      await updatePrimaryResponsible(patientId, primaryResponsible);
    },
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess();
    },
  });

  const { t } = useLingui();

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        mutate(formData.primaryResponsible);
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Uppdaterar patientansvarig`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : undefined}
          <Form.Row>
            <div className={styles.select}>
              {isPendingEmployees ? (
                <Loading message={t`Hämtar medarbetare`} />
              ) : isErrorEmployees ? (
                <ErrorMessage message={t`Kunde inte hämta medarbetare`} />
              ) : (
                <Select
                  label={t`Patientansvarig`}
                  hideLabel
                  {...register("primaryResponsible", {
                    required: {
                      value:
                        fieldConfig ===
                        informationFieldConfigValueSchema.enum.required,
                      message: t`Välj patientansvarig`,
                    },
                  })}
                  errorMessage={errors.primaryResponsible?.message}
                >
                  <option value={NO_EMPLOYEE}>
                    <Trans>Ingen</Trans>
                  </option>
                  {employees.map((employee) => (
                    <option value={employee.id} key={employee.id}>
                      {employeeName(employee, {
                        employeeNameFormat: "full",
                      })}
                    </option>
                  ))}
                </Select>
              )}
            </div>
          </Form.Row>
          {isDirty ? (
            <FilledButton type="submit">
              <Trans>Spara ändringar</Trans>
            </FilledButton>
          ) : (
            <></>
          )}
        </>
      )}
    </Form>
  );
};
